import React, { useCallback, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Container,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link as RouterLink, useLocation, useSearchParams } from "react-router-dom";
import { getEmailError } from "../utils/emailValidation";
import {
  DEFAULT_SUPPORT_EMAIL,
  SUPPORT_CONTACT_ITEMS,
  SUPPORT_SEND_FAILURE_MESSAGE,
  SUPPORT_SEND_SUCCESS_MESSAGE,
  buildSupportDiagnosticBody,
  buildSupportMailto,
  copySupportEmailDetails,
} from "../errors/supportContacts";
import { sendSupportReport } from "../services/supportReportApi";
import { useColorMode } from "../theme";

/**
 * Sticky Contact us rail: phone and sales numbers stay visible while the
 * left column (send form + email) scrolls.
 */
function ContactUsPanel() {
  return (
    <Paper
      component="aside"
      variant="outlined"
      aria-labelledby="support-contact-heading"
      data-testid="support-contact-panel"
      sx={{
        p: { xs: 2, md: 2.5 },
        position: { md: "sticky" },
        top: { md: 72 },
        alignSelf: { md: "start" },
      }}
    >
      <Typography
        id="support-contact-heading"
        variant="h6"
        component="h2"
        sx={{ fontSize: "1.05rem", mb: 0.5 }}
      >
        Contact us
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Prefer to talk? Reach a regional desk or sales.
      </Typography>
      <Stack spacing={1.75} divider={<Box sx={{ borderBottom: 1, borderColor: "divider" }} />}>
        {SUPPORT_CONTACT_ITEMS.map((item) => (
          <Box key={item.label}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 0.25 }}
            >
              {item.label}
            </Typography>
            <Link
              href={item.href}
              underline="hover"
              variant="body2"
              sx={{ fontWeight: 600, wordBreak: "break-word" }}
            >
              {item.value}
            </Link>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

/**
 * Support page: left column is the write/send flow; right column is a sticky
 * Contact us panel. On small screens the panel stacks below the form.
 */
export default function SupportPage({ homePath = "/" } = {}) {
  const { mode } = useColorMode();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [copyState, setCopyState] = useState("");
  const [userNote, setUserNote] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [replyToError, setReplyToError] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [showMailBackup, setShowMailBackup] = useState(false);

  const novaLogo = mode === "dark" ? "/nova-logo-dark.png" : "/nova-logo.png";

  // Prefer the failing page/request carried in the query over this page's own URL.
  const diagnostics = useMemo(() => {
    return {
      kind: searchParams.get("kind") || "",
      status: searchParams.get("status") || "",
      requestId: searchParams.get("request_id") || "",
      code: searchParams.get("code") || "",
      method: searchParams.get("method") || "",
      url: searchParams.get("url") || "",
      message: searchParams.get("message") || "",
      path: searchParams.get("page") || location.pathname + location.search,
      timestamp: searchParams.get("ts") || new Date().toISOString(),
    };
  }, [location.pathname, location.search, searchParams]);

  const mailtoHref = buildSupportMailto(diagnostics);
  const diagnosticPreview = buildSupportDiagnosticBody(diagnostics);
  const hasDiagnostics =
    Boolean(diagnostics.requestId) ||
    Boolean(diagnostics.kind) ||
    Boolean(diagnostics.status) ||
    Boolean(diagnostics.url) ||
    Boolean(diagnostics.message);

  const handleCopy = useCallback(async () => {
    try {
      await copySupportEmailDetails(diagnostics);
      setCopyState("Copied — paste into any email to support.");
    } catch {
      setCopyState("Could not copy. Select the diagnostic text below instead.");
    }
  }, [diagnostics]);

  /**
   * Send support email via the outage-safe client; on failure show mail-app backup.
   */
  const handleSendReport = useCallback(async () => {
    if (replyTo.trim()) {
      const emailError = getEmailError(replyTo, "Reply email");
      if (emailError) {
        setReplyToError(emailError);
        return;
      }
    }
    setReplyToError("");
    setSending(true);
    setSendResult(null);
    setShowMailBackup(false);
    try {
      await sendSupportReport({
        ...diagnostics,
        userNote,
        replyTo,
      });
      setSendResult({
        severity: "success",
        message: SUPPORT_SEND_SUCCESS_MESSAGE,
      });
    } catch {
      setSendResult({
        severity: "error",
        message: SUPPORT_SEND_FAILURE_MESSAGE,
      });
      setShowMailBackup(true);
      try {
        await copySupportEmailDetails(diagnostics);
      } catch {
        // Clipboard may be blocked; mailto still available.
      }
    } finally {
      setSending(false);
    }
  }, [diagnostics, replyTo, userNote]);

  return (
    <Box
      component="main"
      data-testid="support-page"
      sx={{
        height: "100dvh",
        overflowY: "auto",
        overflowX: "hidden",
        bgcolor: "background.default",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <Box
        component="header"
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          position: "sticky",
          top: 0,
          zIndex: 2,
        }}
      >
        <Container
          maxWidth="md"
          sx={{
            py: 1.25,
            px: { xs: 2, sm: 3 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box
            component={RouterLink}
            to={homePath}
            aria-label="Nova home"
            sx={{ display: "flex", alignItems: "center", lineHeight: 0 }}
          >
            <Box
              component="img"
              src={novaLogo}
              alt="Nova"
              sx={{ height: { xs: 26, sm: 30 }, width: "auto", display: "block" }}
            />
          </Box>
          <Button
            component={RouterLink}
            to={homePath}
            variant="text"
            data-testid="support-page-home-link"
            sx={{ minWidth: "auto", px: 1 }}
          >
            Back
          </Button>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 2.5, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 280px" },
            gap: { xs: 2.5, md: 3 },
            alignItems: "start",
          }}
        >
          <Stack spacing={2.5} sx={{ minWidth: 0 }}>
            <Box>
            
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem" }, mb: 0.75 }}
              >
                Contact support
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Send a report from this page. If that does not work, email us
                from your inbox.
              </Typography>
            </Box>

            <Paper
              component="section"
              variant="outlined"
              aria-labelledby="support-send-heading"
              sx={{ p: { xs: 2, sm: 2.5 }, position: "relative" }}
            >
              <Typography
                id="support-send-heading"
                variant="h6"
                component="h2"
                sx={{ fontSize: "1.05rem", mb: 0.5 }}
              >
                Send a message
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Optional details help us investigate. You can send with the
                fields empty.
              </Typography>

              <Stack spacing={1.75}>
                <TextField
                  label="What were you doing? (optional)"
                  value={userNote}
                  onChange={(e) => setUserNote(e.target.value)}
                  multiline
                  minRows={3}
                  maxRows={8}
                  fullWidth
                  size="small"
                  inputProps={{
                    maxLength: 2000,
                    "data-testid": "support-page-note-input",
                  }}
                />
                <TextField
                  label="Your email for a reply (optional)"
                  value={replyTo}
                  onChange={(e) => {
                    setReplyTo(e.target.value);
                    if (replyToError) setReplyToError("");
                  }}
                  onBlur={() => {
                    if (!replyTo.trim()) {
                      setReplyToError("");
                      return;
                    }
                    setReplyToError(getEmailError(replyTo, "Reply email"));
                  }}
                  error={Boolean(replyToError)}
                  helperText={replyToError}
                  type="email"
                  fullWidth
                  size="small"
                  inputProps={{ "data-testid": "support-page-reply-input" }}
                />

                {/* Honeypot — hidden from users, bots often fill it */}
                <Box
                  component="input"
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  sx={{
                    position: "absolute",
                    width: 1,
                    height: 1,
                    left: "-10000px",
                    opacity: 0,
                  }}
                  defaultValue=""
                />

                <Button
                  variant="contained"
                  onClick={handleSendReport}
                  disabled={sending}
                  data-testid="support-page-send-button"
                  sx={{ alignSelf: { xs: "stretch", sm: "flex-start" }, minHeight: 44 }}
                >
                  {sending ? "Sending…" : "Send support email"}
                </Button>

                {sendResult ? (
                  <Alert
                    severity={sendResult.severity}
                    data-testid="support-page-send-status"
                  >
                    {sendResult.message}
                  </Alert>
                ) : null}

                {showMailBackup ? (
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      You can also open your mail app or copy the details.
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button
                        variant="outlined"
                        component="a"
                        href={mailtoHref}
                        data-testid="support-page-mailto-button"
                      >
                        Open mail app
                      </Button>
                      <Button
                        variant="text"
                        onClick={handleCopy}
                        data-testid="support-page-copy-button"
                      >
                        Copy email details
                      </Button>
                    </Stack>
                  </Stack>
                ) : null}

                {copyState ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    data-testid="support-page-copy-status"
                  >
                    {copyState}
                  </Typography>
                ) : null}
              </Stack>
            </Paper>

            <Paper
              component="section"
              variant="outlined"
              aria-labelledby="support-email-heading"
              sx={{ p: { xs: 2, sm: 2.5 } }}
            >
              <Typography
                id="support-email-heading"
                variant="h6"
                component="h2"
                sx={{ fontSize: "1.05rem", mb: 0.5 }}
              >
                Email us directly
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Use this address if you prefer to write from your own inbox.
              </Typography>
              <Link
                href={`mailto:${DEFAULT_SUPPORT_EMAIL}`}
                underline="hover"
                variant="body1"
                sx={{ fontWeight: 600, wordBreak: "break-word", display: "inline-block" }}
              >
                {DEFAULT_SUPPORT_EMAIL}
              </Link>
              {!showMailBackup ? (
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="text"
                    onClick={handleCopy}
                    data-testid="support-page-copy-button"
                    sx={{ px: 0 }}
                  >
                    Copy email details
                  </Button>
                </Box>
              ) : null}
              {hasDiagnostics ? (
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    mt: 1.5,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ minHeight: 40, "& .MuiAccordionSummary-content": { my: 0.5 } }}
                  >
                    <Typography variant="body2">Include diagnostic info</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        fontSize: 12,
                        lineHeight: 1.45,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                      data-testid="support-page-diagnostics"
                    >
                      {diagnosticPreview}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ) : null}
            </Paper>
          </Stack>

          <ContactUsPanel />
        </Box>
      </Container>
    </Box>
  );
}
